import {
  productDataTransformer,
  type IProduct,
  type OptionMap,
  type ProductData,
  type Slot,
} from '@commerce-product-webcomponents/sdk';
import { useState } from 'react';
import { getDesignatedOptionMap } from '..';
import {
  DerivedAvailabilityStatusComponentGroupKey,
  ProductCardComponent,
  VariantDrawer,
  includesDerivedAvailabilityStockStatus,
} from '../../Molecules';
import { updatedSelectedVariantOptionChoice } from './bundleComponentUtil';
import { Operation } from './types/enums';
import type { BundleProductCardTypes, SlotDataType } from './types/propsTypes';

export const BundleProductCard = ({
  data,
  quantityHandler,
  cardType,
  slot,
  handlePriceUpdateOnVariantUpdate,
  parentStockStatus,
  bundleDataState,
  showSubText,
  dateSubText,
  skipChecksForButton,
  showZeroAsFree = false,
  showPreLaunch,
}: BundleProductCardTypes): React.JSX.Element => {
  const [closeDrawer, setCloseDrawer] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct>(data);
  const defaultVariantOptionMap = getDesignatedOptionMap(data.productId, data.variantData);
  const [variantName, setVariantName] = useState<string>(defaultVariantOptionMap?.optionChoice || '');

  const onHandleClick = (
    _e: React.KeyboardEvent | React.MouseEvent | null,
    drawerState: boolean,
    selectedOptionChoice?: OptionMap,
    productId?: string,
    slotId?: number,
  ) => {
    setCloseDrawer(drawerState);
    if (selectedOptionChoice && !drawerState) {
      const slots = [...(bundleDataState.slots as SlotDataType[])];
      const selectedSlotProducts = slots?.find((s: Slot) => s.id === slotId)?.products;
      const selectedProduct = selectedSlotProducts?.find((product) => product.items[0].itemNumber === productId);
      const updatedSlot = slots.map((slot: Slot) => {
        if (slotId === slot.id) {
          return {
            ...slot,
            products: slot.products?.map((product: ProductData) => {
              if (productId === product.items[0].itemNumber) {
                return updatedSelectedVariantOptionChoice(product, selectedOptionChoice);
              } else {
                return { ...product };
              }
            }),
          };
        } else {
          return { ...slot };
        }
      });
      setSelectedProduct(productDataTransformer(selectedProduct as ProductData, showPreLaunch));
      setVariantName(selectedOptionChoice?.optionChoice ?? '');
      handlePriceUpdateOnVariantUpdate({ ...bundleDataState, slots: updatedSlot });
    }
  };

  const maxCountForPool = (): number => Number(slot.maxQty) - Number(slot.totalQty) + productCount;

  const disabledProductForPool = (): boolean => Number(slot.maxQty) === Number(slot.totalQty) && productCount === 0;

  const productCount =
    slot?.products?.find((item) => item.items[0].itemNumber === selectedProduct.productId)?.quantity || 0;

  const disabledConditionForButton =
    disabledProductForPool() ||
    (!skipChecksForButton &&
      (includesDerivedAvailabilityStockStatus(
        selectedProduct.derivedAvailabilityStatus,
        DerivedAvailabilityStatusComponentGroupKey.CTA_DISABLED_STATUSES,
      ) ||
        includesDerivedAvailabilityStockStatus(parentStockStatus as string)));

  return (
    <>
      {closeDrawer && (
        <VariantDrawer
          link={selectedProduct?.mainLink || ''}
          alt={selectedProduct.alt}
          brandName={selectedProduct.brandName}
          productName={selectedProduct.productName}
          productPrice={selectedProduct.productPrice}
          currency={selectedProduct.currency}
          productId={selectedProduct.productId}
          productSize={selectedProduct.productSize}
          onClose={(_e, selectedOptionChoice: OptionMap | undefined) =>
            onHandleClick(null, false, selectedOptionChoice, selectedProduct.productId, slot.id)
          }
          variantGroup={selectedProduct.variantData}
          derivedAvailabilityStatus={selectedProduct.derivedAvailabilityStatus}
          showZeroAsFree={showZeroAsFree}
          productData={selectedProduct}
          showPreLaunch={showPreLaunch}
        />
      )}
      <ProductCardComponent
        brandName={selectedProduct.brandName}
        productName={selectedProduct.productName}
        link={selectedProduct.mainLink || ''}
        alt={selectedProduct.alt}
        productId={selectedProduct.productId}
        productSize={selectedProduct.productSize}
        derivedAvailabilityStatus={selectedProduct.derivedAvailabilityStatus}
        firstAvailableFutureDate={selectedProduct.firstAvailableFutureDate}
        showSubText={showSubText}
        dateSubText={dateSubText}
        cardType={cardType}
        productQuantity={slot.minQty}
        productCount={productCount}
        variantName={variantName}
        maxCount={maxCountForPool()}
        disabled={disabledConditionForButton}
        onQuantityDecrease={() => {
          quantityHandler(selectedProduct.productId, Operation.MINUS, slot);
        }}
        onQuantityIncrease={() => {
          quantityHandler(selectedProduct.productId, Operation.ADD, slot);
        }}
        handleDropdown={() => {
          onHandleClick(null, true);
        }}
        currency={selectedProduct.currency}
        showLinkForProductCard={false}
        productType={selectedProduct.productType}
        showZeroAsFree={showZeroAsFree}
      />
    </>
  );
};
